<script setup lang="ts">
import { useFilteredGeojsonLayers } from "@/composables/geojsonConfig";
import { resolvePageHeading } from "@/utils/pageHeading";

const page = usePage();

const route = useRoute();
// Extract the first part of the URL path
const firstPartOfPath = route.path.split("/")[1].trim();
const showQuizModal = ref(false);
const toggleQuizModal = () => {
  showQuizModal.value = !showQuizModal.value;
};

const closeQuizModal = () => {
  showQuizModal.value = false;
};

const pageTitle = computed(
  () =>
    String(page.value?.title ?? "").trim() ||
    String(page.value?.meta?.title ?? "").trim() ||
    resolvePageHeading(page.value),
);

// Extract "Full", "Westerly", or "Easterly" from page title
const extractedRouteType = computed(() => {
  const title = pageTitle.value || "";
  const match = title.match(/\b(Full|Westerly|Easterly)\b/i);
  return match ? match[1] : "Full"; // Default to "Full" if not found
});

// Check if a mapbox map is available for this trek
const trekTitleForMap = computed(() => {
  return `${page.value?.parentPageTitle} ${extractedRouteType.value} Route`;
});

const geojsonLayers = useFilteredGeojsonLayers(
  page.value?.trekLocation || page.value?.parentTrekLocation,
);

const hasMapboxMap = computed(() => {
  const matchingLayer = geojsonLayers.value.find(
    (layer: any) =>
      `${layer.title} ${layer.route ?? ""}`.trim().toLowerCase() ===
      trekTitleForMap.value.trim().toLowerCase(),
  );
  return !!matchingLayer;
});

// Get fallback image - check for trekMapGraphic, featureImage, or coverImage
const fallbackImage = computed(() => {
  const p = page.value;
  return p?.trekMapGraphic || p?.featureImage || p?.coverImage || null;
});

const packagesTrekTitle = computed(
  () =>
    String(page.value?.parentPageTitle ?? "").trim() ||
    String(page.value?.meta?.title ?? "").trim() ||
    pageTitle.value,
);
</script>

<template>
  <Teleport to="body">
    <Transition name="pop" appear>
      <div v-if="showQuizModal" v-cloak class="modal quiz-modal">
        <div class="close-modal" @click="closeQuizModal"></div>
        <AppFitnessQuiz />
      </div>
    </Transition>
    <Transition name="fade" appear>
      <div
        v-if="showQuizModal"
        v-cloak
        class="backdrop"
        @click="closeQuizModal"
      ></div>
    </Transition>
  </Teleport>
  <div class="trek-page-intro">
    <div class="container">
      <AppNavBreadcrumb />
      <section class="trek-intro-wrap map-box-wrap">
        <div class="trek-map-graphic">
          <div class="map-box-section">
            <AppMapsMapboxShell
              v-if="hasMapboxMap"
              :trek-title="trekTitleForMap"
              :trek-title-short="`${page?.parentPageTitle}`"
              :trek-page="true"
            />
            <NuxtPicture
              v-else-if="fallbackImage"
              loading="lazy"
              :src="fallbackImage.url"
              :width="fallbackImage.width"
              :height="fallbackImage.height"
              :alt="fallbackImage.alt || page?.title || 'Trek map'"
              sizes="sm:100vw md:50vw lg:600px"
            />
          </div>
        </div>
        <div class="trek-intro-text">
          <h3>{{ pageTitle }}</h3>
          <div v-html="page?.trekIntro" />
          <section class="trek-in-brief">
            <h4>Trek in Brief</h4>
            <AppTrekInBrief />
          </section>
        </div>
      </section>
    </div>
  </div>

  <section class="trek-itinerary content">
    <div class="container">
      <h3>Trek Itinerary</h3>
      <AppTrekItinerary />

      <section v-if="page?.parentPageTemplate === 'treks'">
        <AppTrekUsefulInfo
          :trek-page="page?.trekPage"
          :trek-uri="page?.parentPageUri"
        />
      </section>
    </div>
  </section>
  <section id="isThisTrekForYou">
    <div class="container">
      <h2>Is this trek for you?</h2>

      <p>Find out with our quick fitness quiz ...</p>
      <div class="btn-wrap">
        <NuxtLink class="btn" @click.prevent="toggleQuizModal">
          TAKE THE FITNESS QUIZ
        </NuxtLink>
        <NuxtLink
          v-if="page?.parentPageTitle === 'Walker\'s Haute Route'"
          to="/walkers-haute-route/fitness-preparation"
          class="btn"
        >
          FITNESS PREPARATION
        </NuxtLink>
        <NuxtLink
          v-else
          :to="`/${firstPartOfPath}/fitness-preperation`"
          class="btn"
        >
          FITNESS PREPARATION
        </NuxtLink>
      </div>
    </div>
  </section>

  <section class="trek-packages">
    <div class="container">
      <h3>Trek Packages</h3>
      <AppTrekPackagesApi :trek-title="packagesTrekTitle" />
    </div>
  </section>
</template>
